import queryString from 'query-string';
import u, { DeleteUserActions, UserActionTypes } from './userActionTypes';
import a, { AddPlanActionTypes, isAddPlanResponse } from '../addPlan/types/addPlanActions';
import { UserState } from './types';
import r, { isAddUserLoginResponse, RegisterActionTypes } from '../register/types/registerActions';

export function getSearchQuery(from?: string, csrfToken?: string): string {
  if (from || csrfToken)
    return `?${queryString.stringify(
      { csrfToken, from },
      { skipEmptyString: true, skipNull: true }
    )}`;
  return '';
}

export function getRedirectURL(destinationURL?: string, csrfToken?: string): string | undefined {
  if (
    !destinationURL ||
    (destinationURL.startsWith('http') && destinationURL.startsWith(window.location.origin))
  )
    return destinationURL;

  const parsed = queryString.parseUrl(destinationURL);
  const from =
    parsed.query && parsed.query.from ? encodeURIComponent(parsed.query.from as string) : '';
  const search = getSearchQuery(from, csrfToken);
  return `${parsed.url}${search}`;
}

export const defaultUserState: UserState = {
  isLoggedIn: false,
  isFetching: false,
  hasFetchedUser: false,
  redirectTo: '',
  csrfToken: localStorage.getItem('csrfToken') || '',
  currentUser: undefined,
  authBaseURL: '',
  isPasswordResendSuccess: false,
  passwordResetSuccess: false,
  changePasswordSuccess: false,
  isUserDeleted: false,
  isCaseMember: false,
  accessLevel: '',
  isBiometricAvailable: false,
};

const userReducer = (
  state: UserState = defaultUserState,
  action: UserActionTypes | AddPlanActionTypes | RegisterActionTypes | DeleteUserActions
) => {
  switch (action.type) {
    case u.GET_USER_PENDING:
      return { ...state, isFetching: true };
    case u.GET_USER_REJECTED:
      return { ...state, isFetching: false, hasFetchedUser: true, isLoggedIn: false };
    case u.GET_USER_FULFILLED: {
      if (!action.payload.data?.info?.roles || !Array.isArray(action.payload.data.info.roles)) {
        return {
          ...state,
          isFetching: false,
          hasFetchedUser: true,
          isLoggedIn: false,
          currentUser: undefined,
        };
      }
      return {
        ...state,
        isFetching: false,
        hasFetchedUser: true,
        isLoggedIn: true,
        currentUser: {
          ...action.payload.data,
          isPasswordExpired: action.payload.data.info?.isPasswordExpired,
          isOnboardingComplete: action.payload.data.info?.isOnboardingComplete,
          enableNotification: action.payload.data.info?.enableNotification,
          fullName: action.payload.data.info?.fullName,
          roles: action.payload.data.info?.roles,
        },
        redirectTo: !action.payload.data.info?.isOnboardingComplete ? '/onboarding' : '/home',
      };
    }

    case u.COMPLETE_ONBOARDING: {
      const { currentUser } = state;
      // eslint-disable-next-line
      if (currentUser)
        return {
          ...state,
          currentUser: {
            ...currentUser,
            isOnboardingComplete: true,
          },
        };
      return state;
    }

    case u.REDIRECT_TO:
      return { ...state, redirectTo: action.meta };

    case u.CLEAR_REDIRECT:
      return { ...state, redirectTo: '' };

    case u.LOGOUT_REJECTED:
    case u.LOGOUT_FULFILLED: {
      const fullName = localStorage.getItem('fullName') ?? '';
      localStorage.clear();
      localStorage.setItem('fullName', fullName);
      localStorage.setItem('currentSessionTime', '');
      return {
        ...state,

        isFetching: false,
        isLoggedIn: false,
        csrfToken: '',
        currentUser: undefined,
      };
    }

    case u.LOGIN_PENDING:
      // localStorage.clear();
      return { ...state, isFetching: true };
    case u.LOGIN_REJECTED: {
      return {
        ...state,
        isFetching: false,
        isLoggedIn: false,
        csrfToken: '',
        currentUser: undefined,
      };
    }
    case u.LOGIN_FULFILLED: {
      const { csrfToken, user } = action.payload.data;
      // const { email } = JSON.parse(action.payload.config.data);

      const isCaseMember =
        user?.roles &&
        user?.roles.includes('medivi-casesmember') &&
        user?.roles.includes('medivi-casesubmissionmember');
      localStorage.setItem('csrfToken', csrfToken);
      localStorage.setItem('email', user?.email ? user?.email : '');
      localStorage.setItem('fullName', user.fullName);
      localStorage.setItem('isHardSignIn', JSON.stringify(false));

      let redirectTo = action.meta.destinationURL;

      if (action.meta.application === 'memberApp') {
        redirectTo = !user.isOnboardingComplete ? '/onboarding' : action.meta.destinationURL;
      }

      return {
        ...state,
        isFetching: false,
        hasFetchedUser: true,
        csrfToken,
        isLoggedIn: true,
        currentUser: {
          ...user,
          isOnboardingComplete: user.isOnboardingComplete ?? false,
        },
        redirectTo,
        changePasswordSuccess: false,
        passwordResetSuccess: false,
        isCaseMember,
      };
    }
    case u.CHANGE_PASSWORD_REJECTED: {
      return {
        ...state,
        csrfToken: '',
        redirectTo: '',
        changePasswordSuccess: false,
      };
    }
    case u.CHANGE_PASSWORD_FULFILLED: {
      localStorage.clear();
      return {
        ...state,
        isFetching: false,
        csrfToken: '',
        isLoggedIn: true,
        redirectTo: '',
        changePasswordSuccess: true,
      };
    }
    case u.VERIFY_EMAIL_PENDING: {
      return { ...state, isFetching: true };
    }
    case u.VERIFY_EMAIL_REJECTED: {
      return { ...state, isFetching: false, redirectTo: '/' };
    }
    case u.VERIFY_EMAIL_FULFILLED: {
      const { csrfToken, email, isProfileCreated } = action.payload.data;
      const redirectTo = isProfileCreated ? '/' : '/create-profile';
      if (csrfToken) localStorage.setItem('csrfToken', csrfToken);
      if (email) localStorage.setItem('email', email);
      return {
        ...state,
        isFetching: false,
        redirectTo,
        csrfToken,
        currentUser: {
          userID: state.currentUser?.userID || '',
          roles: state.currentUser?.roles || [],
          fullName: state.currentUser?.fullName || '',
          isOnboardingComplete: state.currentUser?.isOnboardingComplete || false,
          email,
          isPasswordExpired: state.currentUser?.isPasswordExpired || false,
        },
      };
    }

    case u.REQUEST_PASSWORD_RESET_PENDING: {
      return { ...state, isFetching: true };
    }
    case u.REQUEST_PASSWORD_RESET_REJECTED: {
      return { ...state, isFetching: false };
    }
    case u.REQUEST_PASSWORD_RESET_FULFILLED: {
      return {
        ...state,
        isFetching: false,
        redirectTo: action.meta.destinationURL,
        isPasswordResendSuccess: action.meta.isResend,
      };
    }

    case u.VERIFY_PASSWORD_RESET_REJECTED: {
      return { ...state, redirectTo: '/login', isFetching: false };
    }
    case u.VERIFY_PASSWORD_RESET_PENDING: {
      return { ...state, isFetching: true };
    }
    case u.VERIFY_PASSWORD_RESET_FULFILLED: {
      localStorage.setItem('csrfToken', action.payload.data.csrfToken);
      localStorage.setItem('fullName', action.payload.data.fullName);
      return {
        ...state,
        isFetching: false,
        redirectTo: '/update-password',
        csrfToken: action.payload.data.csrfToken,
      };
    }

    case u.UPDATE_PASSWORD_PENDING: {
      return { ...state, isFetching: true };
    }
    case u.UPDATE_PASSWORD_REJECTED: {
      return { ...state, isFetching: false };
    }
    case u.UPDATE_PASSWORD_FULFILLED: {
      const { csrfToken, user } = action.payload.data;
      localStorage.setItem('csrfToken', csrfToken);
      localStorage.setItem('fullName', action.payload.data.fullName);

      return {
        ...state,
        isFetching: false,
        redirectTo: '',
        csrfToken,
        passwordResetSuccess: true,
        currentUser: user,
        isLoggedIn: true,
      };
    }

    case u.SET_IS_PASSWORD_RESEND_SUCCESS: {
      return {
        ...state,
        isPasswordResendSuccess: action.payload,
      };
    }

    case u.ADD_CURRENT_USER_ROLE: {
      const { userID, role } = action.payload;

      if (userID !== state.currentUser?.userID) {
        return state;
      }

      return {
        ...state,
        currentUser: {
          ...state.currentUser,
          roles: [...(state.currentUser?.roles || []), role],
        },
      };
    }

    case u.REMOVE_CURRENT_USER_ROLE: {
      const { userID, role } = action.payload;

      if (userID !== state.currentUser?.userID) {
        return state;
      }

      return {
        ...state,
        currentUser: {
          ...state.currentUser,
          roles: (state.currentUser?.roles || []).filter(r => r !== role),
        },
      };
    }

    case u.TOGGLE_NOTIFICATION_FULFILLED: {
      const { currentUser } = state;
      // eslint-disable-next-line
      if (currentUser) {
        return {
          ...state,
          currentUser: {
            ...currentUser,
            enableNotification: action.meta.enableNotification,
          },
        };
      }
      return state;
    }

    case u.GET_AUTH_PENDING:
      return { ...state, isFetching: true };
    case u.GET_AUTH_FULFILLED: {
      const { user, authBaseURL, error } = action.payload.data;
      let isCaseMember = false;
      if (user && user.roles) {
        isCaseMember =
          user.roles.includes('medivi-casesmember') &&
          user.roles.includes('medivi-casesubmissionmember');
      }
      return {
        ...state,
        isFetching: false,
        hasFetchedUser: true,
        isLoggedIn: error === '',
        currentUser: user,
        authBaseURL,
        isCaseMember,
      };
    }
    case u.GET_AUTH_REJECTED: {
      return {
        ...state,
        isFetching: false,
        hasFetchedUser: true,
        isLoggedIn: false,
        csrfToken: '',
        currentUser: undefined,
      };
    }

    case r.ADD_USER_FULFILLED: {
      if (action.payload !== null && isAddUserLoginResponse(action.payload.data)) {
        const { csrfToken, user } = action.payload.data;
        const isCaseMember =
          user.roles &&
          user.roles.includes('medivi-casesmember') &&
          user.roles.includes('medivi-casesubmissionmember');
        localStorage.setItem('csrfToken', csrfToken);
        return {
          ...state,
          csrfToken,
          currentUser: user,
          isLoggedIn: true,
          isCaseMember,
          redirectTo: action.meta.redirectTo || '',
        };
      }
      return { ...state, redirectTo: action.meta.redirectTo || '' };
    }

    case a.ADD_PLAN_FULFILLED: {
      if (isAddPlanResponse(action.payload.data))
        return { ...state, redirectTo: action.meta.redirectTo || '' };
      return state;
    }

    case u.CREATE_PROFILE_PENDING: {
      return { ...state, isFetching: true };
    }
    case u.CREATE_PROFILE_REJECTED: {
      return { ...state, isFetching: false };
    }
    case u.CREATE_PROFILE_FULFILLED: {
      const { csrfToken, user } = action.payload.data;
      localStorage.setItem('csrfToken', csrfToken);
      const isCaseMember =
        user.roles.includes('medivi-casesmember') &&
        user.roles.includes('medivi-casesubmissionmember');
      return {
        ...state,
        isFetching: false,
        csrfToken,
        isLoggedIn: true,
        currentUser: user,
        isCaseMember,
        redirectTo: getRedirectURL(action.meta.destinationURL, csrfToken) ?? '',
      };
    }
    case u.DELETE_USER_PENDING: {
      return { ...state, isFetching: true };
    }
    case u.DELETE_USER_REJECTED: {
      return { ...state, isFetching: false };
    }
    case u.DELETE_USER_FULFILLED: {
      localStorage.clear();
      return {
        ...state,
        isFetching: false,
        isLoggedIn: false,
        isUserDeleted: true,
        csrfToken: '',
        currentUser: undefined,
        redirectTo: '/register',
      };
    }

    case u.UPDATE_USER_STATE: {
      return {
        ...state,
        isUserDeleted: false,
      };
    }

    case u.VERIFY_OTP:
      return { ...state, isFetching: true };
    case u.VERIFY_OTP_PENDING: {
      return {
        ...state,
        isFetching: false,
      };
    }
    case u.VERIFY_OTP_REJECTED: {
      return { ...state, isFetching: false };
    }
    case u.VERIFY_OTP_FULFILLED: {
      let redirectTo = action.meta.destinationURL;

      if (action.meta.application === 'memberApp') {
        redirectTo = state.currentUser?.isOnboardingComplete
          ? action.meta.destinationURL
          : '/onboarding';
      }

      return {
        ...state,
        isFetching: false,
        currentUser: {
          ...state.currentUser,
          isMFANeeded: false,
        },
        redirectTo,
      };
    }

    default: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const status = (action as any)?.payload?.response?.status;
      if (action.type.endsWith('_REJECTED') && status === 401) {
        return {
          ...state,
          isLoggedIn: false,
        };
      }
      return state;
    }
  }
};

export default userReducer;
